import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Star, MessageSquare } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';

export const Stars = ({ value, size = 'h-4 w-4', onChange, testid }) => (
  <div className="inline-flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={onChange ? `${testid}-${n}` : undefined}
        className={`${onChange ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}>
        <Star className={`${size} ${n <= Math.round(value || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`} />
      </button>
    ))}
  </div>
);

export function ProductReviews({ productId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], avg: null, count: 0, can_review: false });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [productId, user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (window.location.hash === '#avis') setTimeout(() => document.getElementById('avis')?.scrollIntoView({ behavior: 'smooth' }), 400); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/products/${productId}/reviews`, { rating, comment }); toast.success('Merci pour votre avis !'); setComment(''); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <section id="avis" className="lg:col-span-2 card-elev rounded-3xl p-6 scroll-mt-24" data-testid="product-reviews">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><MessageSquare className="h-6 w-6 text-amber-400" />Avis clients</h2>
        <div className="flex items-center gap-2" data-testid="product-rating-summary">
          <Stars value={data.avg} />
          <span className="font-bold">{data.avg ?? '—'}</span>
          <span className="text-slate-400 text-sm">({data.count} avis)</span>
        </div>
      </div>
      {data.can_review && (
        <form onSubmit={submit} className="mt-5 p-4 rounded-2xl bg-slate-900/60 border border-amber-500/30 space-y-3" data-testid="product-review-form">
          <div className="font-bold text-sm">Vous avez reçu ce produit — donnez votre avis</div>
          <Stars value={rating} size="h-7 w-7" onChange={setRating} testid="review-stars" />
          <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={500} placeholder="Qualité, conformité à la photo, livraison…"
            className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none text-sm" />
          <Button type="submit" disabled={busy} data-testid="review-submit" className="btn-brand text-white font-bold">Publier mon avis</Button>
        </form>
      )}
      {data.already_reviewed && <div className="mt-4 text-sm text-emerald-400" data-testid="review-done">Merci, votre avis a été publié.</div>}
      <div className="mt-5 grid md:grid-cols-2 gap-3">
        {data.reviews.length === 0 && <div className="text-slate-500 text-sm" data-testid="no-reviews">Pas encore d'avis. Les acheteurs peuvent noter ce produit après livraison.</div>}
        {data.reviews.map((r) => (
          <div key={r.review_id} data-testid={`review-${r.review_id}`} className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-bold text-sm">{r.buyer_name}</div>
              <Stars value={r.rating} size="h-3.5 w-3.5" />
            </div>
            {r.comment && <p className="mt-2 text-sm text-slate-300">{r.comment}</p>}
            <div className="mt-2 text-[11px] text-slate-500">{new Date(r.created_at).toLocaleDateString()} · Achat vérifié</div>
          </div>
        ))}
      </div>
    </section>
  );
}
